import { valuationUrl } from 'common/config/urls';
import { ValuationByAllocation, ValuationsApproach } from 'common/types/valuation';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import { packDescriptionsForPdf } from 'pages/Valuations/util/packDescriptionsForPdf';
import { sortGpcComparisonForPdfDescriptions } from 'pages/Valuations/util/sortComparisonsForPdfExport';
import {
  VALUATIONS_BACKSOLVE_APPROACH,
  VALUATIONS_PUBLIC_COMPANIES_APPROACH,
} from 'pages/ValuationsAllocation/common/constants/valuations';
import toPdfExportTabId from 'pages/ValuationsAllocation/util/toPdfExportTabId/toPdfExportTabId';
import { CompanyPdfPageEntry } from './types';

export type ApproachExportItemPart = 'main' | 'performanceMetrics' | 'descriptions' | 'marketAdjustment';

// One row of the dialog list: a single checkbox that maps to one or more pages of one approach.
export interface ApproachExportItem {
  id: string;
  label: string;
  approachKey: string;
  part: ApproachExportItemPart;
  // Descriptions rows are pinned after every other row, so they print at the end of the PDF.
  isPinnedLast: boolean;
  pages: CompanyPdfPageEntry[];
}

export const getApproachExportItemId = (approachKey: string, part: ApproachExportItemPart) =>
  `approach:${approachKey}:${part}`;

// An item with no explicit choice counts as selected, so approaches that load late start selected.
export const isApproachItemSelected = (approachOptions: Record<string, boolean>, id: string) =>
  approachOptions[id] !== false;

const getApproachKey = (approach: ValuationsApproach) => String(approach.id ?? approach.panelId);

/**
 * One checkbox row per Public Comps / Backsolve approach part, named after the approach the user gave it.
 */
const getApproachExportItems = (valuationInfo?: ValuationByAllocation | null): ApproachExportItem[] => {
  const approaches = (valuationInfo?.valuations_approaches ?? []).filter(approach => !approach.is_deleted);
  const items: ApproachExportItem[] = [];

  approaches.forEach(approach => {
    const approachKey = getApproachKey(approach);
    const approachTab = toPdfExportTabId('approach', approach.approach_type, approach.id ?? approach.panelId);

    const addItem = (
      part: ApproachExportItemPart,
      label: string,
      pages: CompanyPdfPageEntry[],
      isPinnedLast = false
    ) => {
      if (pages.length === 0) {
        return;
      }
      items.push({ id: getApproachExportItemId(approachKey, part), label, approachKey, part, isPinnedLast, pages });
    };

    if (
      approach.approach_type === VALUATIONS_BACKSOLVE_APPROACH &&
      (approach.valuations_approach_backsolve?.applied_methodologies?.length ?? 0) > 0
    ) {
      const approachTitle = approach.name ?? PdfExportTargets.BACKSOLVE.title;
      addItem('main', approachTitle, [
        {
          urlBuilder: valuationUrl,
          target: { title: approachTitle, id: PdfExportTargets.BACKSOLVE.id, tab: approachTab },
        },
      ]);
      if (approach.valuations_approach_backsolve?.adjust_for_market) {
        const ledgerTitle = `Market Adjustment Ledger - ${approachTitle}`;
        addItem('marketAdjustment', `${approachTitle} - Market Adjustment Ledger`, [
          {
            urlBuilder: valuationUrl,
            target: {
              title: ledgerTitle,
              id: PdfExportTargets.BACKSOLVE_MARKET_ADJUSTMENT_LEDGER.id,
              tab: approachTab,
            },
          },
        ]);
      }
    }

    if (approach.approach_type === VALUATIONS_PUBLIC_COMPANIES_APPROACH) {
      const comparisons = approach.valuations_approach_gpc?.gpc_comparison ?? [];
      const approachTitle = approach.name ?? PdfExportTargets.PUBLIC_COMPS.title;

      if (comparisons.length > 0) {
        addItem('main', approachTitle, [
          {
            urlBuilder: valuationUrl,
            target: { title: approachTitle, id: PdfExportTargets.PUBLIC_COMPS.id, tab: approachTab },
          },
        ]);
        addItem('performanceMetrics', `${approachTitle} - Performance Metrics`, [
          {
            urlBuilder: valuationUrl,
            target: {
              title: `${approachTitle} - Performance Metrics`,
              id: PdfExportTargets.PUBLIC_COMPS_PERFORMANCE_METRICS.id,
              tab: approachTab,
            },
          },
        ]);
      }

      const chunks = packDescriptionsForPdf(
        sortGpcComparisonForPdfDescriptions(comparisons),
        comparison => comparison.business_description
      );
      addItem(
        'descriptions',
        `${approachTitle} - Descriptions`,
        chunks.map((_, index) => ({
          urlBuilder: valuationUrl,
          target: {
            title: PdfExportTargets.PUBLIC_COMPS_DESCRIPTIONS.title,
            id: PdfExportTargets.PUBLIC_COMPS_DESCRIPTIONS.id,
            tab: approachTab,
          },
          exportDescriptionPage: String(index),
        })),
        true
      );
    }
  });

  return items;
};

export default getApproachExportItems;
